const RESERVATION_MESSAGE_KEY = 'windowReservationMessage';

class FulfilmentMethodWizardController {
    constructor($scope, $window, $timeout, settings, moveFocus, sessionStorage, loadingIndicatorService, shopperService, fulfilmentService, addressService, loginService, shopPageStateService) {
        this.$scope = $scope;
        this.$window = $window;
        this.$timeout = $timeout;
        this.settings = settings;
        this.sessionStorage = sessionStorage;
        this.loadingIndicatorService = loadingIndicatorService;
        this.shopperService = shopperService;
        this.fulfilmentService = fulfilmentService;
        this.addressService = addressService;
        this.loginService = loginService;
        this.moveFocus = moveFocus;
        this.shopPageStateService = shopPageStateService;
    }

    $onInit() {
        // The watchers below open method selection panel or fulfilment timetable when a certain event is fired.
        //
        // This is needed for the cart component and the checkout component to trigger the fulfilment method wizard to change the state when
        // errors, such as delivery window/method restricted products cannot be delivered, occurs in the process. The problem is that the
        // components responsible for the trigger is not a direct parent/child of the wizard, which make it difficult to manage the dataflow.
        //
        // All events listed should be fired from the fulfilment service only. Please refer to fulfilment.service.js for its usage.
        //
        // Of course, if possible please kindly avoid using events.
        this.changeMethodWatcher = this.$scope.$on('fulfilment:changeMethod', () => this.openFulfilmentMethodSelection());
        this.changeTimeslotWatcher = this.$scope.$on('fulfilment:changeTimeslot', () => this.openTimeslots());

        this.fulfilmentSteps = this.fulfilmentService.fulfilmentSteps;
        this.modalMessage = this.sessionStorage.getAndRemove(RESERVATION_MESSAGE_KEY);

        this.deliveryMethodMessage = this.settings.trolley.deliveryMethodMessage
            .replace(' {Firstname}', this.shopperService.IsGuest
                ? ''
                : ` ${this.shopperService.shopper.FirstName}`);

        this.fulfilmentOptions = this.allowDecideLater ? this.fulfilmentService.fulfilmentOptions : this.fulfilmentService.checkoutFulfilmentOptions;

        // Make sure the wizard will go to the correct step when on checkout (allow decide later === false)
        if (!this.allowDecideLater) {
            this.$timeout(() => this.initializeFulfilmentStep());
        }

        this.shouldShowDeliveryWindowErrorModal = this.shouldShowDeliveryWindowErrorModal !== undefined ? this.shouldShowDeliveryWindowErrorModal : true;
    }

    $onDestroy() {
        // Deregister watchers
        this.changeMethodWatcher();
        this.changeTimeslotWatcher();
    }

    initializeFulfilmentStep() {
        if (this.fulfilmentService.isNotSet()) {
            this.openFulfilmentMethodSelection();
        } else if (!this.fulfilmentService.savedAddress) {
            this.openAddressesSelection();
        } else if (!this.hasSelectedTimeslot()) {
            this.openTimeslots();
        }
    }

    setFulfilmentMethod(fulfilmentMethod) {
        this.fulfilmentService.selectedFulfilmentMethod = fulfilmentMethod;

        this.changeAnswer(this.fulfilmentSteps.fulfilmentMethod);
        this.currentStep = this.fulfilmentSteps.fulfilmentMethod;
        this.fulfilmentService.savedAddress = null;

        this.$timeout(() => {
            this.currentStep = fulfilmentMethod === this.fulfilmentService.fulfilmentOptions.none
                ? null
                : this.fulfilmentSteps.address;

            if (fulfilmentMethod === this.fulfilmentService.fulfilmentOptions.none) {
                this.fulfilmentService.clearFulfilmentInfo();
            }
        });
    }

    ensureLoggedIn(callback) {
        if (this.shopperService.IsGuest) {
            this.loginService.promptForLogin(this.loginService.LoginToSetDeliveryOrPickupAddressMessage, undefined, undefined, undefined, callback);
        } else {
            callback();
        }
    }

    saveFulfilment(fulfilmentInfo) {
        const wasGuest = this.shopperService.IsGuest;

        if (fulfilmentInfo.address) {
            this.ensureLoggedIn(() => {
                this.loadingIndicatorService.startLoading(true, this.settings.delivery.deliveryWindowReloadingMessage);
                this.fulfilmentService.saveFulfilment(fulfilmentInfo)
                    .then(response => this.onSaveFulfilment(response, wasGuest))
                    .catch(() => this.loadingIndicatorService.finishLoading());
            });
        } else {
            this.currentStep = null;
            this.fulfilmentService.clearAddressAndTime();
            this.shopPageStateService.isCartOpen = false;
        }
    }

    onSaveFulfilment(response, wasGuest) {
        if (wasGuest) return;

        if (response && response.Message) {
            this.sessionStorage.set(RESERVATION_MESSAGE_KEY, response.Message);
        }

        this.$window.location.reload();
    }

    deliveryChange(fulfilmentInfo) {
        this.fulfilmentService.savedAddress = fulfilmentInfo.address;

        if (fulfilmentInfo.fulfilmentWindow) {
            this.fulfilmentService.reservedDate = fulfilmentInfo.fulfilmentWindow.date;
            this.fulfilmentService.reservedTime = fulfilmentInfo.fulfilmentWindow.time;
        }
    }

    hasSelectedTimeslot() {
        return !!(this.fulfilmentService.reservedDate && this.fulfilmentService.reservedTime && this.fulfilmentService.reservedDate.DateText && this.fulfilmentService.reservedTime.TimeWindow);
    }

    selectedAddressText() {
        return this.fulfilmentService.savedAddress
            ? this.fulfilmentService.savedAddress.AddressText
            : this.fulfilmentService.selectedFulfilmentMethod.addressText;
    }

    billingAddressText() {
        const primaryAddress = this.addressService.getPrimaryAddress();

        return primaryAddress
            ? primaryAddress.AddressText
            : 'select billing address';
    }

    openFulfilmentMethodSelection() {
        this.changeAnswer(this.fulfilmentSteps.fulfilmentMethod);
        this.currentStep = this.fulfilmentSteps.fulfilmentMethod;
        this.fulfilmentService.savedAddress = null;
        this.moveFocus('#fulfilmentMethod');
    }

    openAddressesSelection() {
        this.changeAnswer(this.fulfilmentSteps.address);
        this.currentStep = this.fulfilmentSteps.address;
        this.moveFocus('#fulfilmentAddress');
    }

    openTimeslots() {
        this.changeAnswer(this.fulfilmentSteps.timeslot);
        this.currentStep = this.fulfilmentSteps.timeslot;
        this.moveFocus('#fulfilmentTimetable');
    }

    openBilling() {
        this.changeAnswer(this.fulfilmentSteps.billing);
        this.currentStep = this.fulfilmentSteps.billing;
        this.moveFocus('#fulfilmentBillingAddress');
    }

    changeAnswer(fulfilmentStep) {
        if (this.onChangeAnswer) {
            this.onChangeAnswer({ stepChanged: fulfilmentStep });
        }
    }

    shouldShowBillingQuestionaire() {
        return this.shouldShowBilling
            && this.fulfilmentService.selectedFulfilmentMethod === this.fulfilmentService.fulfilmentOptions.pickup
            && this.fulfilmentService.reservedTime
            && this.currentStep !== this.fulfilmentSteps.address
            && this.currentStep !== this.fulfilmentSteps.timeslot;
    }
}

FulfilmentMethodWizardController.$inject = ['$scope', '$window', '$timeout', 'Settings', 'MoveFocus', 'SessionStorageWrapper', 'LoadingIndicatorService', 'ShopperService', 'FulfilmentService', 'AddressService', 'LoginService', 'ShopPageStateService'];

const fulfilmentMethodWizardComponentDefinition = {
    bindings: {
        shouldShowBilling: '<?',
        allowDecideLater: '<',
        isActive: '<',
        onChangeAnswer: '&?',
        shouldGetDynamicTimetable: '<?',
        shouldShowDeliveryWindowErrorModal: '<?',
    },
    controller: FulfilmentMethodWizardController,
    template: require('./fulfilment-method-wizard.component.html').trim(),
};

module.exports = {
    FulfilmentMethodWizardController,
    fulfilmentMethodWizardComponentDefinition,
};



// WEBPACK FOOTER //
// ./src/fulfilment/fulfilmentmethod/fulfilment-method-wizard.component.js